import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSearch,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../../lib/supabaseClient";
import Loader from "../Loader";
import Styles from "../../styles/Analytics.module.css";

const Managers = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [results, setResults] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");

  const loadStaff = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: loadError } = await supabase
        .from("profiles")
        .select("id, username, email, role, is_premium, created_at")
        .in("role", ["manager", "admin"])
        .order("role", { ascending: true })
        .order("created_at", { ascending: true });

      if (loadError) throw loadError;
      setStaff(data || []);
    } catch (loadError) {
      setError(loadError.message || "Failed to load managers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const searchUsers = async () => {
    const term = search.trim();
    if (!term) {
      setResults([]);
      return;
    }

    setSearching(true);
    setError("");
    try {
      const { data, error: searchError } = await supabase
        .from("profiles")
        .select("id, username, email, role, is_premium")
        .or(`username.ilike.%${term}%,email.ilike.%${term}%`)
        .limit(25);

      if (searchError) throw searchError;
      setResults(data || []);
    } catch (searchError) {
      setError(searchError.message || "Failed to search users");
    } finally {
      setSearching(false);
    }
  };

  const setRole = async (userId, role) => {
    setSavingId(userId);
    setError("");
    try {
      const { data, error: roleError } = await supabase.rpc(
        "admin_set_user_role",
        { p_user_id: userId, p_role: role },
      );
      if (roleError) throw roleError;
      if (data && !data.success) {
        throw new Error(data.error || "Failed to update role");
      }
      await loadStaff();
      if (search.trim()) await searchUsers();
    } catch (roleError) {
      setError(roleError.message || "Failed to update role");
    } finally {
      setSavingId(null);
    }
  };

  if (loading && staff.length === 0) {
    return (
      <div className={Styles.container}>
        <Loader text="Loading managers" />
      </div>
    );
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.headerRow}>
        <div>
          <h1 className={Styles.title}>Managers</h1>
          <p className={Styles.welcomeMessage}>
            Managers can add locations and automatically receive Pro access.
            They cannot access analytics, users, or other admin tools.
          </p>
        </div>
        <div className={Styles.headerActions}>
          <button
            type="button"
            className={Styles.backButton}
            onClick={() => navigate("/admin")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Admin
          </button>
        </div>
      </div>

      {error && (
        <div className={Styles.errorBox} role="alert">
          {error}
        </div>
      )}

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>Current Managers & Admins</h2>
          <p className={Styles.sectionSubtitle}>
            Admins keep full access. Managers get only Add Location + Pro.
          </p>
        </div>
      </div>

      <div className={`${Styles.card} ${Styles.tableCard}`}>
        <div className={Styles.tableWrapper}>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Pro</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((person) => (
                <tr key={person.id}>
                  <td>
                    <Link
                      className={Styles.userLink}
                      to={`/analytics/users/${person.id}`}
                    >
                      @{person.username}
                    </Link>
                  </td>
                  <td>
                    <Link
                      className={Styles.emailLink}
                      to={`/analytics/users/${person.id}`}
                    >
                      {person.email}
                    </Link>
                  </td>
                  <td>{person.role}</td>
                  <td>
                    <span
                      className={`${Styles.statusBadge} ${
                        person.is_premium
                          ? Styles.statusPro
                          : Styles.statusFree
                      }`}
                    >
                      {person.is_premium ? "Pro" : "Free"}
                    </span>
                  </td>
                  <td>
                    {person.role === "manager" ? (
                      <button
                        type="button"
                        className={Styles.tableAction}
                        onClick={() => setRole(person.id, "user")}
                        disabled={savingId === person.id}
                      >
                        Demote to user
                      </button>
                    ) : (
                      <span className={Styles.topMeta}>Protected admin</span>
                    )}
                  </td>
                </tr>
              ))}
              {staff.length === 0 && (
                <tr>
                  <td colSpan="5" className={Styles.emptyTableCell}>
                    No managers yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>Promote a User</h2>
          <p className={Styles.sectionSubtitle}>
            Search by username or email, then make the user a manager. They
            become Pro automatically.
          </p>
        </div>
        <div className={Styles.headerActions}>
          <label className={Styles.searchLabel}>
            <FontAwesomeIcon icon={faSearch} />
            <input
              className={Styles.searchInput}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void searchUsers();
              }}
              placeholder="Search username or email"
              aria-label="Search users to promote"
            />
          </label>
          <button
            type="button"
            className={Styles.refreshButton}
            onClick={searchUsers}
            disabled={searching}
          >
            <FontAwesomeIcon icon={faSearch} spin={searching} />
            Search
          </button>
        </div>
      </div>

      <div className={`${Styles.card} ${Styles.tableCard}`}>
        <div className={Styles.tableWrapper}>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Pro</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {results.map((person) => (
                <tr key={person.id}>
                  <td>@{person.username}</td>
                  <td>{person.email}</td>
                  <td>{person.role}</td>
                  <td>
                    <span
                      className={`${Styles.statusBadge} ${
                        person.is_premium
                          ? Styles.statusPro
                          : Styles.statusFree
                      }`}
                    >
                      {person.is_premium ? "Pro" : "Free"}
                    </span>
                  </td>
                  <td>
                    {person.role === "manager" ? (
                      <span className={Styles.topMeta}>Already manager</span>
                    ) : person.role === "admin" ? (
                      <span className={Styles.topMeta}>Admin</span>
                    ) : (
                      <button
                        type="button"
                        className={Styles.tableAction}
                        onClick={() => setRole(person.id, "manager")}
                        disabled={savingId === person.id}
                      >
                        <FontAwesomeIcon icon={faUserTie} /> Make manager
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {results.length === 0 && (
                <tr>
                  <td colSpan="5" className={Styles.emptyTableCell}>
                    Search for a user to promote.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Managers;
